/**
 * How far to scroll a container when a drag reaches its edge (M1-11 follow-up).
 *
 * Pure arithmetic, deliberately separated from the DOM plumbing that calls it.
 * jsdom reports every dimension as zero and never composites, so nothing about
 * auto-scroll can be tested through a rendered board — but the *policy* (when
 * to scroll, which way, how fast, and when to stop) is just numbers, and
 * numbers are testable. What the browser has to verify is then only the wiring:
 * that the right container is found and that the ticks actually fire.
 */

/** How much room a container has left to scroll, per direction, in pixels. */
export interface ScrollRoom {
  left: number;
  right: number;
  up: number;
  down: number;
}

export interface EdgeScrollOptions {
  /** How close to an edge the pointer must come before scrolling starts. */
  margin?: number;
  /** The furthest this call may scroll, at full intensity. */
  maxStep?: number;
}

/**
 * A band wide enough to enter deliberately and hard to clip by accident. Much
 * narrower and a dispatcher aiming at the last visible column triggers a
 * scroll they did not want; much wider and most of a narrow column is a
 * scroll zone.
 */
export const DEFAULT_EDGE_MARGIN = 56;

/** ~900 px/s at the hook's tick rate — a screen's width in about a second. */
export const DEFAULT_MAX_SPEED_PER_SECOND = 900;

/**
 * The scroll to apply for one tick, given where the pointer is inside a
 * container and how much room that container has left.
 *
 * Speed scales with how deep into the edge band the pointer is, so easing up
 * slows down instead of stopping dead — a fixed speed makes the board feel
 * like it is fighting you. A pointer past the edge entirely (which happens
 * when a drag leaves the container) is treated as full intensity rather than
 * as no longer relevant.
 *
 * Never scrolls further than `room` allows, so a container already at its end
 * returns zero rather than the caller having to check. That is what stops the
 * tick loop spinning on a board that cannot move.
 */
export function edgeScrollDelta(
  rect: { left: number; top: number; right: number; bottom: number },
  pointer: { x: number; y: number },
  room: ScrollRoom,
  options: EdgeScrollOptions = {},
): { dx: number; dy: number } {
  const margin = options.margin ?? DEFAULT_EDGE_MARGIN;
  const maxStep = options.maxStep ?? 16;

  // 0 when the pointer is outside the band, rising to 1 at the edge and
  // staying there beyond it.
  const intensity = (distanceFromEdge: number) => {
    if (distanceFromEdge >= margin) {
      return 0;
    }
    return Math.min(1, Math.max(0, (margin - distanceFromEdge) / margin));
  };

  const towardLeft = intensity(pointer.x - rect.left);
  const towardRight = intensity(rect.right - pointer.x);
  const towardTop = intensity(pointer.y - rect.top);
  const towardBottom = intensity(rect.bottom - pointer.y);

  // A container narrower than two margins has both bands overlapping. The
  // stronger side wins, and a dead centre pointer nets to zero rather than
  // juddering between them.
  let dx = 0;
  if (towardRight > towardLeft) {
    dx = Math.min(maxStep * towardRight, Math.max(0, room.right));
  } else if (towardLeft > towardRight) {
    dx = -Math.min(maxStep * towardLeft, Math.max(0, room.left));
  }

  let dy = 0;
  if (towardBottom > towardTop) {
    dy = Math.min(maxStep * towardBottom, Math.max(0, room.down));
  } else if (towardTop > towardBottom) {
    dy = -Math.min(maxStep * towardTop, Math.max(0, room.up));
  }

  return { dx, dy };
}
